import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "@unform/web";
import * as Yup from "yup";
import intl from "react-intl-universal";
import { TbAlertTriangle, TbArrowLeft } from "react-icons/tb";
import { convertToCents, formatCurrency, stringToDecimal } from "../../../Utils/formatters";
import getValidationErrors from "../../../Utils/getValidationErrors";
import { getPixTransactionalLimits } from "../../../Services/pixLimitsApi";
import { resolveCustomerPixOverlayCents } from "../../../Utils/computePixOverlayFee";
import { SelectedAccountBalance } from "./SelectedAccountBalance";
import { PixTransferAmountInput } from "./PixTransferAmountInput";
import { getAccountTypeLabel } from "../../../Constants/accountTypes";
import { resolvePixKeyApiType } from "../../../Utils/detectPixKeyType";

/**
 * Pix transfer amount step.
 * Source account is always the home active account (no mid-flow switch).
 *
 * Continuar is disabled when:
 * - balance is still loading
 * - balance fetch failed / unavailable
 * - amount + overlay (when Cliente paga) > spendable
 * - amount > Pix period available (day/night window) — not the per-Pix teto (lote covers that)
 * - initiate payment request is in flight
 * - no source account
 */
export const PixTransferInfoStep = ({
  sourceAccount,
  sourceAccountBalance,
  sourceAccountBalanceCents,
  sourceAccountBalanceLoading,
  sourceAccountBalanceError = false,
  sourceAccountShowLimit = false,
  sourceAccountFormattedAvailable,
  sourceAccountFormattedLimit,
  sourceAccountFormattedRemainingLimit,
  destinationDraft,
  handleFetchInitialPayment,
  loadingFetchInitialPayment,
  successFetchInitialPayment,
  errorFetchInitialPayment,
  errorMessage,
  onSetTransferAmount,
  onTypedAmountCentsChange,
  onBack,
  onSuccess,
  onCanContinueChange,
  formId,
  hideFooter = false,
  feeQuote = null,
}) => {
  const formRef = useRef(null);
  const hasAdvancedRef = useRef(false);
  const [amountInput, setAmountInput] = useState("");
  const [periodAvailableCents, setPeriodAvailableCents] = useState(null);

  const amountCents = useMemo(() => convertToCents(amountInput || "0"), [amountInput]);

  useEffect(() => {
    onTypedAmountCentsChange?.(amountCents);
  }, [amountCents, onTypedAmountCentsChange]);

  const overlayCents = useMemo(() => {
    const fromSchedule = resolveCustomerPixOverlayCents({
      amountCents,
      feePayer: feeQuote?.feePayer,
      feePercent: feeQuote?.feePercent,
      feeFixed: feeQuote?.feeFixed,
    });
    if (fromSchedule > 0) return fromSchedule;
    const payer = String(feeQuote?.feePayer || "customer").trim().toLowerCase();
    if (payer === "instance") return 0;
    return Math.max(0, Math.trunc(Number(feeQuote?.taxAmountCents) || 0));
  }, [amountCents, feeQuote]);

  const totalDebitCents = amountCents + overlayCents;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const limits = await getPixTransactionalLimits();
        if (cancelled) return;
        const active = (limits.items || []).find(
          (item) => item.activeNow && !item.comingSoon,
        );
        setPeriodAvailableCents(
          active?.availableCents != null ? Number(active.availableCents) : null,
        );
      } catch {
        if (!cancelled) setPeriodAvailableCents(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const balanceUnavailable =
    sourceAccountBalanceLoading ||
    sourceAccountBalanceError ||
    sourceAccountBalanceCents == null;

  const insufficientBalance =
    !balanceUnavailable && amountCents > 0 && totalDebitCents > sourceAccountBalanceCents;

  // Per-Pix teto must not block Continuar — lote splits on Confirmação.
  const exceedsPeriodLimit =
    periodAvailableCents != null
    && Number.isFinite(periodAvailableCents)
    && amountCents > 0
    && amountCents > periodAvailableCents;

  const canContinue =
    !!sourceAccount &&
    !loadingFetchInitialPayment &&
    !balanceUnavailable &&
    !insufficientBalance &&
    !exceedsPeriodLimit;

  useEffect(() => {
    onCanContinueChange?.(canContinue);
  }, [canContinue, onCanContinueChange]);

  useEffect(() => {
    return () => onCanContinueChange?.(false);
  }, [onCanContinueChange]);

  useEffect(() => {
    if (!successFetchInitialPayment) {
      hasAdvancedRef.current = false;
      return;
    }
    if (hasAdvancedRef.current) return;
    hasAdvancedRef.current = true;
    onSuccess?.();
  }, [successFetchInitialPayment, onSuccess]);

  const buildPayload = useCallback(
    (cents) => {
      if (destinationDraft?.mode === "qrcode") {
        return {
          mode: "qrcode",
          qr_code: destinationDraft.qrCode,
          account_id: sourceAccount || undefined,
          amount: cents,
        };
      }

      const payload = {
        mode: "key",
        amount: cents,
        account_id: sourceAccount || undefined,
        key: {
          type:
            resolvePixKeyApiType(destinationDraft.pixKeyType, destinationDraft.keyValue) ||
            destinationDraft.pixKeyType,
          value: destinationDraft.keyValue,
        },
      };

      if (destinationDraft.documentToValidate) {
        payload.documentToValidate = destinationDraft.documentToValidate;
      }

      return payload;
    },
    [destinationDraft, sourceAccount],
  );

  const handleSubmit = useCallback(
    async (formData) => {
      const cents = convertToCents(formData.value);
      const decimalValue = stringToDecimal(formData.value);
      const fromSchedule = resolveCustomerPixOverlayCents({
        amountCents: cents,
        feePayer: feeQuote?.feePayer,
        feePercent: feeQuote?.feePercent,
        feeFixed: feeQuote?.feeFixed,
      });
      const payer = String(feeQuote?.feePayer || "customer").trim().toLowerCase();
      const feeCents =
        fromSchedule > 0
          ? fromSchedule
          : payer === "instance"
            ? 0
            : Math.max(0, Math.trunc(Number(feeQuote?.taxAmountCents) || 0));

      const amountSchema = Yup.object().shape({
        value: Yup.number()
          .typeError("Digite o valor da transferência")
          .required("Digite o valor da transferência")
          .moreThan(0, "Valor deve ser maior que R$ 0,00"),
      });

      try {
        await amountSchema.validate({ value: decimalValue }, { abortEarly: false });

        // Guard: never call POST /v1/ib/pix when balance/period is unknown or insufficient.
        // Per-Pix teto alone must not block — Confirmação shows lote when split applies.
        if (
          sourceAccountBalanceLoading ||
          sourceAccountBalanceError ||
          sourceAccountBalanceCents == null ||
          cents + feeCents > sourceAccountBalanceCents
        ) {
          return;
        }
        if (
          periodAvailableCents != null
          && Number.isFinite(periodAvailableCents)
          && cents > periodAvailableCents
        ) {
          return;
        }

        onSetTransferAmount?.(cents);
        handleFetchInitialPayment(buildPayload(cents));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        }
      }
    },
    [
      buildPayload,
      feeQuote,
      handleFetchInitialPayment,
      onSetTransferAmount,
      periodAvailableCents,
      sourceAccountBalanceCents,
      sourceAccountBalanceError,
      sourceAccountBalanceLoading,
    ],
  );

  return (
    <Form ref={formRef} id={formId} onSubmit={handleSubmit}>
      <section className="pix-transfer-section app-card">
        <h2 className="pix-transfer-section__title">Informações da transferência</h2>
        <p className="pix-transfer-section__subtitle">
          Informe o valor. A transferência usa a conta ativa da home.
        </p>

        <div className="mt-5 space-y-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-500">
              Saldo disponível
            </label>
            <SelectedAccountBalance
              accountLabel={getAccountTypeLabel(sourceAccount)}
              formattedBalance={sourceAccountBalance}
              loading={sourceAccountBalanceLoading}
              showAccountName={false}
              hideLabel
              showLimit={sourceAccountShowLimit}
              formattedAvailable={sourceAccountFormattedAvailable}
              formattedLimit={sourceAccountFormattedLimit}
              formattedRemainingLimit={sourceAccountFormattedRemainingLimit}
            />
          </div>

          <PixTransferAmountInput onAmountChange={setAmountInput} />

          {amountCents > 0 && (overlayCents > 0 || feeQuote != null) && (
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 px-3">
              <div className="flex items-start justify-between gap-3 border-b border-gray-100 py-1.5">
                <span className="shrink-0 text-xs text-gray-500">
                  {intl.get("pix.transfer.feeLabel")}
                </span>
                <span className="min-w-0 text-right text-sm font-semibold text-brand-dark">
                  {overlayCents > 0
                    ? formatCurrency(overlayCents)
                    : intl.get("pix.transfer.noFee")}
                </span>
              </div>
              {overlayCents > 0 && (
                <div className="flex items-start justify-between gap-3 py-1.5">
                  <span className="shrink-0 text-xs text-gray-500">
                    {intl.get("pix.transfer.totalDebitLabel")}
                  </span>
                  <span className="min-w-0 text-right text-sm font-semibold text-brand-dark">
                    {formatCurrency(totalDebitCents)}
                  </span>
                </div>
              )}
            </div>
          )}

          {insufficientBalance && (
            <div
              className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
              role="alert"
            >
              <TbAlertTriangle
                className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  {intl.get("pix.transfer.insufficientBalanceTitle")}
                </p>
                <p className="mt-0.5 text-sm text-amber-800">
                  {overlayCents > 0
                    ? intl.get("pix.transfer.insufficientBalanceWithFee", {
                        available: sourceAccountBalance,
                        total: formatCurrency(totalDebitCents),
                      })
                    : intl.get("pix.transfer.insufficientBalance", {
                        available: sourceAccountBalance,
                      })}
                </p>
              </div>
            </div>
          )}

          {!insufficientBalance && exceedsPeriodLimit && (
            <div
              className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
              role="alert"
            >
              <TbAlertTriangle
                className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  {intl.get("pix.transfer.exceedsPeriodLimitTitle")}
                </p>
                <p className="mt-0.5 text-sm text-amber-800">
                  {intl.get("pix.transfer.exceedsPeriodLimit", {
                    available: formatCurrency(periodAvailableCents),
                  })}
                </p>
              </div>
            </div>
          )}

          {!insufficientBalance && !exceedsPeriodLimit && sourceAccountBalanceError && !sourceAccountBalanceLoading && (
            <div
              className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
              role="alert"
            >
              <TbAlertTriangle
                className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
                aria-hidden="true"
              />
              <p className="text-sm text-amber-900">
                {intl.get("pix.transfer.balanceUnavailable")}
              </p>
            </div>
          )}

          {errorFetchInitialPayment && (
            <p className="text-sm text-red-600">
              {errorMessage?.description ||
                errorMessage?.title ||
                "Não foi possível iniciar a transferência Pix."}
            </p>
          )}
        </div>

        {!hideFooter && (
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn-back inline-flex items-center gap-1.5"
              onClick={onBack}
              disabled={loadingFetchInitialPayment}
            >
              <TbArrowLeft className="h-4 w-4" />
              Voltar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!canContinue}
            >
              {loadingFetchInitialPayment ? "Consultando…" : "Continuar"}
            </button>
          </div>
        )}
      </section>
    </Form>
  );
};
